import React, { useState, useEffect, useCallback } from 'react';
import appwrite, { COLLECTIONS, Query } from '../services/appwrite';
import { notDeleted } from '../lib/records';
import { toast } from 'sonner';
import { 
  ArrowUpDown, 
  Filter,
  Calendar,
  User
} from 'lucide-react';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Timeline component for displaying notes and events
 * Features:
 * - Hover-reveal filter/sort controls
 * - Borderless items with hover background
 * - Vertical line between items
 * - Truncated note content
 * - Click to open full note in overlay
 */
const Timeline = ({ 
  recordType, 
  recordId, 
  onNoteClick,
  onNewNote,
  isHovering = false,
  className = '' 
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const queries = [
        Query.equal('relatedRecordType', recordType),
        Query.equal('relatedRecordId', recordId),
        notDeleted()
      ];

      // Add sorting
      if (sortBy === 'createdAt') {
        queries.push(sortOrder === 'desc' ? Query.orderDesc('$createdAt') : Query.orderAsc('$createdAt'));
      } else if (sortBy === 'updatedAt') {
        queries.push(sortOrder === 'desc' ? Query.orderDesc('$updatedAt') : Query.orderAsc('$updatedAt'));
      }

      const response = await appwrite.listDocuments(COLLECTIONS.NOTES, queries);
      setNotes(response.documents || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [recordType, recordId, sortBy, sortOrder]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes, filterBy]);

  // Realtime subscription for notes — auto-refresh on create/update/delete
  useEffect(() => {
    if (!recordId) return;

    const unsubscribe = appwrite.subscribeToCollection(COLLECTIONS.NOTES, (response) => {
      const payload = response.payload;
      // Only refresh if the note belongs to this record
      if (payload?.relatedRecordId === recordId && payload?.relatedRecordType === recordType) {
        fetchNotes();
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [recordId, recordType, fetchNotes]);

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const truncateNote = (content, maxLength = 150) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Hover-reveal controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {notes.length} {notes.length === 1 ? 'note' : 'notes'}
        </div>
        
        <div 
          className={`flex items-center gap-2 transition-opacity duration-200 ${
            isHovering || isSortMenuOpen || isFilterMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Sort dropdown */}
          <DropdownMenu onOpenChange={setIsSortMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-sm font-semibold">Sort by</div>
              <DropdownMenuItem onClick={() => handleSortChange('createdAt')}>
                Created {sortBy === 'createdAt' && `(${sortOrder === 'desc' ? '↓' : '↑'})`}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSortChange('updatedAt')}>
                Updated {sortBy === 'updatedAt' && `(${sortOrder === 'desc' ? '↓' : '↑'})`}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filter dropdown */}
          <DropdownMenu onOpenChange={setIsFilterMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Filter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterBy('all')}>
                All Notes {filterBy === 'all' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterBy('recent')}>
                Last 7 Days {filterBy === 'recent' && '✓'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* New note button */}
          {onNewNote && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={onNewNote}
            >
              <span className="text-lg leading-none">+</span>
            </Button>
          )}
        </div>
      </div>

      {/* Timeline list */}
      {notes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No notes yet
        </div>
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-0 bottom-0 w-px bg-border" />
          
          {notes.map((note, index) => (
            <div
              key={note.$id}
              className="relative pl-10 pb-6 last:pb-0 group cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onNoteClick(note)}
            >
              {/* Dot on timeline - centered on line and aligned with text */}
              <div className="absolute left-[7.5px] top-[3px] w-[7px] h-[7px] rounded-full bg-primary" />
              
              {/* Note card - negative margin to extend over line/dot */}
              <div 
                className={`rounded-lg p-3 -ml-10 pl-[2.5rem] transition-all duration-200 ${
                  hoveredIndex === index 
                    ? 'bg-accent/50' 
                    : 'bg-transparent'
                }`}
              >
                {/* Header with date and user */}
                <div className="flex items-center gap-3 mb-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {sortBy === 'updatedAt' && note.$updatedAt !== note.$createdAt
                        ? `Updated ${formatDate(note.$updatedAt)}`
                        : formatDate(note.$createdAt)
                      }
                    </span>
                  </div>
                  {note.createdByUser && (
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{note.createdByUser.name || 'Unknown User'}</span>
                    </div>
                  )}
                </div>
                
                {/* Note content */}
                <div className="text-sm whitespace-pre-wrap">
                  {truncateNote(note.content)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Timeline;
